import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { 
    LayoutDashboard, 
    MapPin, 
    Building2, 
    Users, 
    Ticket, 
    Palette, 
    CreditCard,
    Headphones,
    Heart,
    LogOut,
    Menu,
    X,
    BarChart3,
    QrCode,
    UserPlus,
    Settings,
    Shield,
    Globe,
    Languages,
    Package,
    Receipt,
    ShoppingBag,
    MessageCircle,
    Tag
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const adminNav = [
    { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { type: 'section', label: 'Marketplace' },
    { href: '/admin/listings', icon: ShoppingBag, label: 'B2C Listings' },
    { href: '/admin/b2c-orders', icon: Receipt, label: 'B2C Orders' },
    { href: '/admin/discounts', icon: Tag, label: 'Discount Codes' },
    { type: 'section', label: 'Content' },
    { href: '/admin/cities', icon: MapPin, label: 'Cities' },
    { href: '/admin/attractions', icon: Building2, label: 'Attractions' },
    { href: '/admin/content-requests', icon: Package, label: 'Content Requests' },
    { href: '/admin/language-requests', icon: Globe, label: 'Language Requests' },
    { href: '/admin/moderation', icon: Shield, label: 'Moderation' },
    { type: 'section', label: 'Operations' },
    { href: '/admin/operators', icon: Users, label: 'Operators' },
    { href: '/admin/codes', icon: QrCode, label: 'Codes & Vouchers' },
    { href: '/admin/deposits', icon: Receipt, label: 'Deposits' },
    { type: 'section', label: 'Support' },
    { href: '/admin/chat', icon: MessageCircle, label: 'Live Chat' },
    { type: 'section', label: 'System' },
    { href: '/admin/pages', icon: Globe, label: 'Page Manager' },
    { href: '/admin/blog', icon: Receipt, label: 'Travel Insider' },
    { href: '/admin/analytics', icon: BarChart3, label: 'Platform Analytics' },
    { href: '/admin/settings', icon: Settings, label: 'Settings' },
];

// Operator nav is built dynamically based on WL status
const getOperatorNav = (isWhitelabel) => {
    const base = [
        { href: '/operator', icon: LayoutDashboard, label: 'Dashboard' },
        { href: '/operator/codes', icon: Ticket, label: 'Redeem Codes' },
        { href: '/operator/content-requests', icon: Package, label: 'Branded Content' },
        { href: '/operator/analytics', icon: BarChart3, label: 'Analytics' },
        { href: '/operator/team', icon: UserPlus, label: 'Team Members' },
        { href: '/operator/credits', icon: CreditCard, label: 'Credits' },
    ];
    if (isWhitelabel) {
        base.push({ href: '/operator/branding', icon: Palette, label: 'Branding' });
        base.push({ href: '/operator/languages', icon: Languages, label: 'Language Add-ons' });
    }
    return base;
};

const travelerNav = [
    { href: '/my-guides', icon: Headphones, label: 'My Guides' },
    { href: '/redeem', icon: Ticket, label: 'Redeem Code' },
];

export function Sidebar({ type = 'admin' }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const brand = useBrand();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isWhitelabel, setIsWhitelabel] = useState(false);

    useEffect(() => {
        if (type === 'operator') {
            // Check WL status from the /me endpoint
            fetch(`${API}/api/auth/me`, { credentials: 'include' })
                .then(r => r.json())
                .then(data => setIsWhitelabel(!!data.is_whitelabel))
                .catch(() => {});
        }
    }, [type]);

    const navItems = type === 'admin' ? adminNav : type === 'operator' ? getOperatorNav(isWhitelabel) : travelerNav;

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const NavContent = () => (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-white/10">
                <Link to="/" className="flex items-center gap-3">
                    {brand.logo_url ? (
                        <img src={brand.logo_url} alt={brand.platform_name} className="h-8 max-w-[140px] object-contain" />
                    ) : (
                        <div className="w-10 h-10 rounded-md bg-gold flex items-center justify-center">
                            <Headphones className="w-5 h-5 text-[#0A0D14]" />
                        </div>
                    )}
                    <div>
                        {!brand.logo_url && <h1 className="font-outfit font-semibold text-lg text-white">{brand.platform_name}</h1>}
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            {type === 'admin' ? 'Admin' : type === 'operator' ? 'Operator' : 'Traveler'}
                        </p>
                    </div>
                </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
                {navItems.map((item, idx) => {
                    if (item.type === 'section') {
                        return (
                            <div key={`section-${idx}`} className="pt-4 pb-1.5 first:pt-0">
                                <p className="px-4 text-[10px] font-semibold uppercase tracking-widest text-white/30">{item.label}</p>
                            </div>
                        );
                    }
                    const isActive = location.pathname === item.href || 
                        (item.href !== '/' && item.href !== '/admin' && item.href !== '/operator' && location.pathname.startsWith(item.href));
                    const exactActive = item.href === '/admin' || item.href === '/operator'
                        ? location.pathname === item.href
                        : isActive;
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                                "flex items-center gap-3 px-4 py-2.5 rounded-md transition-all duration-200 text-[13px]",
                                exactActive 
                                    ? "bg-gold text-[#0A0D14] font-medium" 
                                    : "text-muted-foreground hover:bg-white/5 hover:text-white"
                            )}
                        >
                            <item.icon className="w-4 h-4" />
                            <span>{item.label}</span>
                        </Link>
                    );
                })}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-3 px-4 py-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-gold font-medium text-sm">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                </div>
                <Button 
                    variant="ghost" 
                    onClick={handleLogout}
                    className="w-full justify-start text-muted-foreground hover:text-white hover:bg-white/5"
                    data-testid="logout-button"
                >
                    <LogOut className="w-5 h-5 mr-3" />
                    Sign Out
                </Button>
            </div>
        </div>
    );

    const isTraveler = type === 'traveler';

    return (
        <>
            {/* Mobile Menu Button - hidden for travelers (they use bottom nav) */}
            {!isTraveler && (
                <button
                    onClick={() => setMobileOpen(!mobileOpen)}
                    className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-[#121620] border border-white/10"
                    data-testid="mobile-menu-toggle"
                >
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            )}

            {/* Mobile Sidebar Overlay */}
            {mobileOpen && !isTraveler && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={cn(
                "fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#121620] border-r border-white/10 transform transition-transform duration-200",
                isTraveler
                    ? "-translate-x-full lg:translate-x-0"
                    : (mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0")
            )}>
                <NavContent />
            </aside>
        </>
    );
}
